import React from 'react';
import { createRoot } from 'react-dom/client';
import FrontendTiptapEditor from '../components/FrontendTiptapEditor.jsx';

class FrontendCommentTiptapInitializer {
  constructor() {
    this.editors = new Map();
    this.observer = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeEditors();
        this.startObserver();
      });
    } else {
      this.initializeEditors();
      this.startObserver();
    }
    // Re-run after Turbo events
    document.addEventListener('turbo:load', () => this.initializeEditors());
    document.addEventListener('turbo:render', () => this.initializeEditors());
    document.addEventListener('turbo:frame-load', () => this.initializeEditors());
    document.addEventListener('turbo:after-stream-render', () => this.initializeEditors());
    document.addEventListener('turbo:before-cache', () => this.cleanup());
    document.addEventListener('turbo:submit-end', () => {
      // After a form submission and Turbo Stream updates, ensure editors are mounted
      setTimeout(() => this.initializeEditors(), 0);
    });
  }

  initializeEditors(root = document) {
    const containers = root.querySelectorAll ? root.querySelectorAll('.fe-react-tiptap-editor') : [];
    // Read data attrs: support both underscore (FE ERB) and hyphen/camelCase (Rails data: {} / admin)
    const getData = (el, key) => {
      const withUnderscore = el.getAttribute(`data-${key}`);
      if (withUnderscore != null && withUnderscore !== '') return withUnderscore;
      const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      return el.dataset[camel] ?? el.getAttribute(`data-${key.replace(/_/g, '-')}`) ?? '';
    };
    const getDataBool = (el, key) => {
      const v = getData(el, key);
      return v === '' || v == null ? true : v !== 'false';
    };
    containers.forEach(container => {
      if (container.dataset.initialized === 'true') return;
      const hiddenInputId = getData(container, 'hidden_input_id');
      // Prefer hidden input value for initial content (avoids broken data attr when HTML has quotes, e.g. admin)
      const hiddenInput = hiddenInputId ? document.getElementById(hiddenInputId) : null;
      const initialContentFromInput = (hiddenInput && hiddenInput.value && hiddenInput.value.trim()) ? hiddenInput.value : '';
      const initialContent = initialContentFromInput || getData(container, 'initial_content') || '';
      const placeholder = getData(container, 'placeholder') || 'Write a comment...';
      const imageIconPath = getData(container, 'image_icon_path') || '';
      const attachmentIconPath = getData(container, 'attachment_icon_path') || '';
      const fileTypeIconPath = getData(container, 'file_type_icon_path') || '/assets/icon-pdf.svg';
      const fileTypeIconPathPpt = getData(container, 'file_type_icon_path_ppt') || '/assets/icon-ppt.svg';
      const fileTypeIconPathPin = getData(container, 'file_type_icon_path_pin') || '/assets/icon-pin.svg';
      const enableImage = getDataBool(container, 'tiptap_image');
      const enableAttachment = getDataBool(container, 'tiptap_attachment');
      const attachmentsBelowRaw = getData(container, 'attachments_below');
      const attachmentsBelow = attachmentsBelowRaw === 'true' || attachmentsBelowRaw === true;

      const root = createRoot(container);
      root.render(
        React.createElement(FrontendTiptapEditor, {
          initialContent,
          placeholder,
          hiddenInputId,
          imageIconPath,
          attachmentIconPath,
          fileTypeIconPath,
          fileTypeIconPathPpt,
          fileTypeIconPathPin,
          enableImage,
          enableAttachment,
          attachmentsBelow,
          onChange: () => {},
          onReady: () => {}
        })
      );
      this.editors.set(container, root);
      container.dataset.initialized = 'true';
    });

    // Clean up any roots whose containers are no longer in DOM
    this.editors.forEach((root, container) => {
      if (!document.body.contains(container)) {
        try { root.unmount(); } catch(e) { /* no-op */ }
        this.editors.delete(container);
      }
    });
  }

  cleanup() {
    this.editors.forEach((root, container) => {
      if (!document.body.contains(container)) {
        try { root.unmount(); } catch(e) { /* no-op */ }
        this.editors.delete(container);
      } else {
        // Reset initialized flag before caching to allow re-init after restore
        container.dataset.initialized = 'false';
      }
    });
  }

  startObserver() {
    if (this.observer) return;
    this.observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // Initialize newly added editors
        m.addedNodes.forEach(node => {
          if (!(node instanceof Element)) return;
          if (node.matches && node.matches('.fe-react-tiptap-editor')) {
            this.initializeEditors(node.parentElement || node);
          } else if (node.querySelectorAll) {
            const inner = node.querySelectorAll('.fe-react-tiptap-editor');
            if (inner.length) this.initializeEditors(node);
          }
        });
        // Clean up removed editors
        m.removedNodes.forEach(node => {
          if (!(node instanceof Element)) return;
          const maybeEditors = node.matches && node.matches('.fe-react-tiptap-editor')
            ? [node]
            : (node.querySelectorAll ? Array.from(node.querySelectorAll('.fe-react-tiptap-editor')) : []);
          maybeEditors.forEach(container => {
            const root = this.editors.get(container);
            if (root) {
              try { root.unmount(); } catch(e) { /* no-op */ }
              this.editors.delete(container);
            }
          });
        });
      }
    });
    this.observer.observe(document.documentElement, { childList: true, subtree: true });
  }
}

window.FrontendCommentTiptapInitializer = FrontendCommentTiptapInitializer;
const frontendTiptapInitializer = new FrontendCommentTiptapInitializer();
export default frontendTiptapInitializer;
